import { ApiService } from './api.service';
import { useSessionStore } from '@host/stores/session.store';

const APP_SLUG = 'office';
const TUTORIAL_VERSION_KEY = 'tutorial_version';

/**
 * The current tour version. Bump this when adding new steps to the tour so
 * that returning users are shown only the new slides (sinceVersion > their
 * lastSeenVersion), while first-time users see all slides.
 */
export const CURRENT_TOUR_VERSION = 2;

// In-memory cache keyed by session ID so the value is scoped to the current
// login session. When a user logs out and another logs in (without a full
// page refresh), the new session ID invalidates the cache automatically.
const sessionCache = new Map<string, number | null>();

/** Coerce a setting value to a positive version number, or null if absent/invalid. */
const toVersion = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Returns the last tour version the current user has completed, or null if
 * they have never seen the tutorial. Cached per session.
 */
export async function getTutorialVersion(): Promise<number | null> {
  const sessionId = useSessionStore.getState().sessionId;

  if (sessionId && sessionCache.has(sessionId)) {
    return sessionCache.get(sessionId)!;
  }

  try {
    const res = await ApiService.get<{ settings?: Record<string, unknown> }>(
      `user-org-preferences/app-settings/${APP_SLUG}`
    );
    const version = toVersion(res?.settings?.[TUTORIAL_VERSION_KEY]);
    if (sessionId) sessionCache.set(sessionId, version);
    return version;
  } catch {
    return null;
  }
}

/**
 * Persist the tour version the user has just completed.
 *
 * Follows the established GET-merge-PUT pattern so unrelated keys survive the
 * write. Skips the PUT if the server already records this version or higher.
 */
export async function markTutorialVersion(version: number): Promise<void> {
  const safe = Math.trunc(version);
  if (!Number.isFinite(safe) || safe < 1) {
    console.warn(`markTutorialVersion: invalid version "${version}", ignoring`);
    return;
  }

  const sessionId = useSessionStore.getState().sessionId;

  // Update cache optimistically so subsequent getTutorialVersion() calls
  // return the new value without waiting for the server round-trip.
  if (sessionId) sessionCache.set(sessionId, safe);

  try {
    const res = await ApiService.get<{ settings?: Record<string, unknown> }>(
      `user-org-preferences/app-settings/${APP_SLUG}`
    );
    const currentSettings: Record<string, unknown> = res?.settings ?? {};
    const serverVersion = toVersion(currentSettings[TUTORIAL_VERSION_KEY]);

    if (serverVersion !== null && serverVersion >= safe) {
      return;
    }

    await ApiService.put(`user-org-preferences/app-settings/${APP_SLUG}`, {
      settings: { ...currentSettings, [TUTORIAL_VERSION_KEY]: safe },
    });
  } catch {
    console.warn('Failed to persist tutorial version to server');
  }
}
